<template>
  <AreaPageContainer
    v-if="area"
  >
      <!-- Content wrapper with header and footer -->
      <AreaPageBody>
        <AreaHeader />

        <!-- Inner Content -->
        <div class="tw-p-10">
          <!-- Title -->
          <div>
            <h2 class="tw-font-georgia tw-font-bold tw-text-5xl">
              Concentration Breakdown
            </h2>
            <AdvisorCoreStripes />
          </div>

          <!-- Text -->
          <p class="tw-font-poppins tw-mt-10">
            {{ area.headerText }}
          </p>

          <!-- Chart -->
          <div class="tw-mt-10">
            <div :class="{'tw-flex': true, 'tw-flex-col': !area.showChartsAsRow}">
              <div
                v-for="(chart, i) in area.chartData"
                :key="i"
                class="tw-grow"
              >
                <div v-if="chart?.library === 'highcharts'">
                  <HighChartsWrapper
                    :name="area.name"
                    :chart-options="chart"
                    export-image
                    @image-changed="(data) => checkupStore.updateSlideImage(data, area.name)"
                    @image-removed="(data) => checkupStore.removeSlideImage(data)"
                  />
                <!-- ChartOptionsSidebar -->
                </div>
                <ChartWrapper
                  v-else
                  canvas-class="tw-max-h-60"
                  :name="area.name"
                  :chart-config="chart"
                  @chartClick="handleChartClick"
                  @imageChanged="(data) => checkupStore.updateChart(data, area.name)"
                />
              </div>
            </div>

            <!-- ChartDetailModal will be replaced by ChartClickthroughs -->
            <ChartClickthroughs
              :click-data="chartClickData"
              :get-table-data="area.clickthroughFunction"
              :portfolio-external-id="checkupStore.portfolio?.external_id"
              :benchmark-external-id="checkupStore.benchmark?.external_id"
              :optimized-portfolio-external-id="checkupStore.optimizedPortfolio?.external_id"
              :portfolio-name="checkupStore.portfolio?.name"
              :benchmark-name="checkupStore.benchmark?.name"
              :optimized-portfolio-name="checkupStore.optimizedPortfolio?.name"
            />
          </div>

          <!-- Disclaimer and Methodology -->
          <div class="tw-flex tw-gap-10">
            <!-- <CheckupDisclaimer class="tw-basis-0 tw-grow tw-bg-white !tw-text-[#353a46] tw-px-0" /> -->

            <div class="tw-flex tw-flex-col tw-basis-0 tw-grow tw-py-10 tw-bg-white !tw-text-[#353a46]">
              <h3 class="tw-text-xl tw-font-bold tw-font-georgia">
                Methodology
              </h3>
              <CheckupMethodologyWrapper
                :component-name="area.methodology"
                display-type="short"
                class="tw-text-xs tw-font-poppins"
              />
              <CheckupMethodologyWrapper
                :component-name="area.methodology"
                display-type="long"
                class="tw-text-xs tw-font-poppins tw-hidden"
              />
            </div>
          </div>
        </div>

        <AreaFooter />
        <CheckupDisclaimer class="tw-mt-16">
          <CheckupMethodologyWrapper
            :component-name="area.methodology"
            display-type="long"
          />
        </CheckupDisclaimer>
      </AreaPageBody>

      <!-- Settings Sidebar -->
      <AreaSidebar
        :area="area"
      >
        <IncludeInPresentationToggle :name="area?.name" />
        <template #other>
          <SpeakersNotes :notes="area?.explanation" />
          <!-- <ChartToggle :options="[{value: 'bar', label: 'Bar', icon: 'fas fa-chart-bar'}, {value: 'box', label: 'Box', icon: 'fas fa-th-large'}]" /> -->
          <!-- <AIChat /> -->
        </template>
      </AreaSidebar>
  </AreaPageContainer>

  <div v-else>
    <p>Loading...</p>
  </div>
</template>


<script>
import { GRADE_COLORS } from '@frontend/ui/features/checkup/helpers/checkupFunctions.js';
import { useChatStore } from '@frontend/ui/stores/chatStore';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { mapStores } from 'pinia';

import AreaPageContainer from '@frontend/ui/features/checkup/components/analytics/shared/AreaPageContainer.vue';
import AreaPageBody from '@frontend/ui/features/checkup/components/analytics/shared/AreaPageBody.vue';
import ChartWrapper from '@frontend/ui/components/charts/ChartWrapper.vue';
import ChartClickthroughs from '@frontend/ui/features/checkup/components/ChartClickthroughs.vue';
import CheckupDisclaimer from '@frontend/ui/features/checkup/components/CheckupDisclaimer.vue';
import ConcentrationBreakdownMethodology from '@frontend/ui/features/checkup/components/methodologies/ConcentrationBreakdownMethodology.vue';

import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';
import AreaSidebar from '@frontend/ui/features/checkup/components/sidebar/AreaSidebar.vue';
import AdvisorCoreStripes from '@frontend/ui/features/checkup/components/AdvisorCoreStripes.vue';
import IncludeInPresentationToggle from '@frontend/ui/features/checkup/components/sidebar/IncludeInPresentationToggle.vue';
import SpeakersNotes from '@frontend/ui/features/checkup/components/sidebar/SpeakersNotes.vue';
import ChartToggle from '@frontend/ui/features/checkup/components/sidebar/ChartToggle.vue';
import AIChat from '@frontend/ui/features/checkup/components/sidebar/AIChat.vue';
import AreaHeader from '@frontend/ui/features/checkup/components/analytics/shared/AreaHeader.vue';
import CheckupMethodologyWrapper from '@frontend/ui/features/checkup/components/methodologies/CheckupMethodologyWrapper.vue';
import AreaFooter from '@frontend/ui/features/checkup/components/analytics/shared/AreaFooter.vue';

export default {
  name: 'MarketCapArea',
  components: {
    AreaPageContainer,
    AreaPageBody,
    ChartWrapper,
    ChartClickthroughs,
    CheckupDisclaimer,
    ConcentrationBreakdownMethodology,
    HighChartsWrapper,
    AreaSidebar,
    AdvisorCoreStripes,
    IncludeInPresentationToggle,
    SpeakersNotes,
    ChartToggle,
    AIChat,
    AreaHeader,
    CheckupMethodologyWrapper,
    AreaFooter,

  },
  props: {
    isActiveTab: Boolean,
  },
  data() {
    return {
      expanded: false,
      GRADE_COLORS,
      chartClickData: null,
    };
  },
  computed: {
    ...mapStores(useChatStore, useCheckupStore),

    area() {
      return this.checkupStore.areasByName?.['Concentration Breakdown'];
    },
    benchmarkName() {
      const name = this.checkupStore.benchmark?.name;
      return name || 'Benchmark';
    },
  },

  watch: {
    // If you are having weird bugs where the active tab seems to be wrong or off by one, double check:
    // :is-active-tab="(i+3) === activeTab" in AutoPortfolioView.vue (the number 3 may be different)
    // 😭
    async isActiveTab() {
      if (this.isActiveTab) {
        const { name, current, proposal, grade } = this.area;
        // await this.chatStore.setContext(mockJSON);

        await this.chatStore.setContext({
          jsonData: {
            currentArea: {
              name,
              [this.checkupStore.portfolio.name]: current,
              [this.benchmarkName]: proposal,
              magnitudeOfDifference: grade,

              // Dear future developer, it would probably be better to have a chatContextData coming from the area object (for all areas).
              // That way it can be easily customized.
              // This is a band-aid to fix the chat for the Holdings Concentration tab.
              holdingsConcentrationBreakdown: name === 'Concentration Breakdown' ? this.checkupStore.portfolio.sector_market_cap_asset_class_breakdown?.['concentration_breakdown'] : undefined,
            },
            // TODO: Sort highest returns to lowest so we are giving the most important info to openai
            portfolio: {
              name: this.checkupStore.portfolio.name,
              holdings: this.checkupStore.portfolio.holdings.map(h => this.createContextFromHolding(h)),
            },
            benchmark: {
              name: this.benchmarkName,
              holdings: this.checkupStore.benchmark.holdings.map(h => this.createContextFromHolding(h)),
            },
          },
        });
      }
    },
  },
  methods: {
    makeValuesPercentages(obj) {
      if (!obj) return {};
      return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key] = `${((value || 0) * 100).toFixed(2)}%`;
        return acc;
      }, {});
    },
    createContextFromHolding(h) {
      return {
        symbol: h.ticker.symbol,
        dollars: h.dollars,
        returns: this.makeValuesPercentages(h.ticker.trailing_returns_monthly_rebalance),
        sectors: this.makeValuesPercentages(h.ticker.security_breakdown?.['Breakdown']),
        expenseRatio: `${(h.ticker.expense_ratio * 100).toFixed(2)}%`,
        assetClasses: this.makeValuesPercentages(h.ticker.security_breakdown?.['AssetType']),
        marketCap: this.makeValuesPercentages(h.ticker.security_breakdown?.['Market Cap']),
        trailingVolatilities: this.makeValuesPercentages(h.ticker.trailing_volatilities),
      };
    },
    // Maybe make callouts in parallel

    handleChartClick(data) {
      this.chartClickData = data;
      console.log('ConcentrationBreakdown.chartClickData', data);
    },
  },
};
</script>


  <style scoped>

  </style>
